import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Bell, FileText, User, DollarSign, Building2, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PONotificationModal = () => {
  const [notifications, setNotifications] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const { user, isAdmin, isSupplyChainManager } = useAuth();
  const { formatCurrency } = useSettings();

  const fetchNotifications = useCallback(async () => {
    // Only fetch for Admin and Supply Chain Manager
    if (!isAdmin() && !isSupplyChainManager()) return;
    
    // Check if notifications were already shown this session
    const sessionKey = `po_notifications_shown_${user?.id}`;
    if (sessionStorage.getItem(sessionKey)) {
      return; // Already shown this session, don't show again
    }
    
    try {
      const response = await axios.get(`${API}/po-notifications`);
      if (response.data.length > 0) {
        setNotifications(response.data);
        setCurrentIndex(0);
        setOpen(true);
        // Mark as shown for this session
        sessionStorage.setItem(sessionKey, 'true');
      }
    } catch (error) {
      console.error('Failed to fetch PO notifications:', error);
    }
  }, [isAdmin, isSupplyChainManager, user?.id]);

  useEffect(() => {
    // Initial fetch only (no polling - show once per session)
    fetchNotifications();
  }, [fetchNotifications]);

  const handleAcknowledge = async () => {
    const notification = notifications[currentIndex];
    if (!notification) return;

    try {
      await axios.post(`${API}/po-notifications/${notification.id}/acknowledge`);
      
      // Move to next notification or close
      if (currentIndex < notifications.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        setOpen(false);
        setNotifications([]);
      }
    } catch (error) {
      console.error('Failed to acknowledge notification:', error);
    }
  };

  const currentNotification = notifications[currentIndex];

  if (!currentNotification) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="po-notification-modal">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Bell className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="font-bold text-lg">New Purchase Order</DialogTitle>
              <DialogDescription>
                Requires your attention
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* PO Number */}
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <FileText className="w-5 h-5 text-slate-500" />
            <div>
              <p className="text-xs text-slate-500">PO Number</p>
              <p className="font-mono font-bold text-slate-900">{currentNotification.po_number}</p>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
              <Building2 className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Supplier</p>
                <p className="text-sm font-medium text-slate-900 truncate">{currentNotification.supplier_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
              <DollarSign className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Total Amount</p>
                <p className="text-sm font-bold text-emerald-600">{formatCurrency(currentNotification.total_amount)}</p>
              </div>
            </div>
          </div>

          {/* Project (if exists) */}
          {currentNotification.project && (
            <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-600">Project</p>
              <p className="text-sm font-medium text-amber-900">{currentNotification.project}</p>
            </div>
          )}

          {/* Requested By */}
          <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
            <User className="w-5 h-5 text-blue-500" />
            <div>
              <p className="text-xs text-blue-600">Submitted by</p>
              <p className="font-medium text-blue-900">{currentNotification.requested_by}</p>
            </div>
            <Badge className="ml-auto bg-blue-100 text-blue-700 hover:bg-blue-100">
              Pending Approval
            </Badge>
          </div>

          {/* Notification count */}
          {notifications.length > 1 && (
            <p className="text-center text-sm text-slate-500">
              Notification {currentIndex + 1} of {notifications.length}
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false);
              // Navigate to PO page
              window.location.href = '/purchase-orders';
            }}
            className="flex-1 rounded-sm"
          >
            View PO Details
          </Button>
          <Button
            onClick={handleAcknowledge}
            className="flex-1 bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="acknowledge-po-btn"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Acknowledge
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PONotificationModal;
